import { useEffect, useMemo, useRef } from "react"

import { requestCoviHighlight } from "@/lib/coviEvents"

import { useTestSetEditorContext } from "../contexts/TestSetEditorContext"
import { EVENTS, useEventDispatcher } from "../event-bus"
import type { EditableTestCase, PendingChanges } from "../model"
import {
  applyPendingRowAction,
  getPendingDiffSummaries,
  removePendingChangeAtIndex,
} from "../utils/copilotSidebarState"
import {
  publishCoviTestSetEditorSnapshot,
  registerCoviTestSetEditor,
  type CoviTestSetEditorAction,
  type CoviTestSetEditorResult,
} from "../utils/coviTestSetEditorBridge"
import { CLEAR_SENTINEL } from "../utils/pendingChangesValidation"

function truncate(value: string | null | undefined, maxLength = 44) {
  if (!value) {
    return null
  }
  return value.length > maxLength
    ? `${value.slice(0, maxLength).trimEnd()}...`
    : value
}

function testCaseLabel(testCase: EditableTestCase | undefined, index: number) {
  return (
    truncate(testCase?.description) ??
    truncate(testCase?.input_str) ??
    `Test case ${index + 1}`
  )
}

function actionTypeFor(action: CoviTestSetEditorAction) {
  switch (action.kind) {
    case "draft_test_cases":
    case "add_test_case":
      return "ADD_NEW"
    case "update_test_case":
      return "EDIT"
    case "delete_test_case":
      return "DELETE"
  }
}

function pendingTypeFor(action: CoviTestSetEditorAction) {
  switch (actionTypeFor(action)) {
    case "ADD_NEW":
      return "ADD"
    case "EDIT":
      return "EDIT"
    case "DELETE":
      return "DELETE"
  }
}

function rowsFor(action: CoviTestSetEditorAction): Record<string, unknown>[] {
  switch (action.kind) {
    case "draft_test_cases":
      return action.args.test_cases.map((testCase) => ({ ...testCase }))
    case "add_test_case":
      return [{ ...action.args }]
    case "update_test_case":
      return [
        {
          id: action.args.test_case_id,
          input_str: action.args.input_str,
          // An empty description means clear to Sofia but no change to the editor.
          description:
            action.args.description === ""
              ? CLEAR_SENTINEL
              : action.args.description,
          expected_behaviors: action.args.expected_behaviors,
          input_type: action.args.input_type,
          script_turns: action.args.script_turns,
        },
      ]
    case "delete_test_case":
      return [{ id: action.args.test_case_id }]
  }
}

function findTestCaseRow(id: string): HTMLElement | null {
  return (
    Array.from(
      document.querySelectorAll<HTMLElement>("[data-test-case-id]"),
    ).find((element) => element.dataset.testCaseId === id) ?? null
  )
}

export function CoviTestSetEditorBridge() {
  const { testSetId, testCases, pendingChanges } = useTestSetEditorContext()
  const dispatch = useEventDispatcher()
  const registrationRef = useRef<symbol | null>(null)
  const stateRef = useRef({ testSetId, testCases, pendingChanges })
  stateRef.current = { testSetId, testCases, pendingChanges }

  // Sofia auto-executes every confirm card from one reply inside a single
  // effect flush, so `stage` runs back to back with no render in between and
  // `pendingChanges` above is still the pre-batch value on every call after the
  // first. Accumulate onto what we last dispatched instead, and drop that the
  // moment the editor renders anything other than it — a save, an undo, or a
  // manual row edit all make the editor authoritative again.
  const stagedRef = useRef<PendingChanges | null>(null)
  if (stagedRef.current !== null && pendingChanges !== stagedRef.current) {
    stagedRef.current = null
  }

  useEffect(() => {
    const stage = (
      action: CoviTestSetEditorAction,
    ): CoviTestSetEditorResult => {
      const current = stateRef.current
      const basePending = stagedRef.current ?? current.pendingChanges
      const actionType = actionTypeFor(action)
      const pendingType = pendingTypeFor(action)
      if (basePending && basePending.type !== pendingType) {
        return {
          ok: false,
          error:
            "Save or undo the current test-case changes before staging a different kind of change.",
        }
      }

      const rows = rowsFor(action)

      let nextPending = basePending
      const stagedIds: string[] = []
      for (const rowData of rows) {
        const result = applyPendingRowAction({
          actionType,
          currentPendingChanges: nextPending,
          rowData,
          testCases: current.testCases,
        })
        if (!result) {
          continue
        }
        nextPending = result.pendingChanges
        if (result.rowMeta.testCaseId) {
          stagedIds.push(result.rowMeta.testCaseId)
        }
      }

      if (!nextPending || stagedIds.length === 0) {
        return {
          ok: false,
          error:
            "Sofia couldn't stage that change. The test case may no longer exist or the proposed values may be unchanged.",
        }
      }

      stagedRef.current = nextPending
      dispatch(EVENTS.PENDING_CHANGES_UPDATE, { changes: nextPending })
      const firstId = stagedIds[0]
      window.setTimeout(() => {
        const row = findTestCaseRow(firstId)
        row?.scrollIntoView({ behavior: "smooth", block: "center" })
        requestCoviHighlight(firstId)
      }, 0)

      const count = stagedIds.length
      // Per-batch wording only, never the editor's cumulative pending total:
      // pending rows can predate the current requested_total request (a
      // one-shot draft left unsaved), so a running total would over-count and
      // hand Sofia false progress. Cumulative gating derives from the
      // transcript's batch sizes instead (see app-covi draftBatchProgress).
      return {
        ok: true,
        detail: `${count} test case${count === 1 ? "" : "s"} staged for review`,
      }
    }

    const { registration, unregister } = registerCoviTestSetEditor({
      stage,
      save: () => {
        const count = stateRef.current.pendingChanges?.data.length ?? 0
        if (count === 0) {
          return {
            ok: false,
            error: "There are no staged test-case changes to save.",
          }
        }
        dispatch(EVENTS.SAVE_REQUEST, {})
        return {
          ok: true,
          detail: `Saving ${count} staged test case${count === 1 ? "" : "s"}`,
        }
      },
      undoAll: () => {
        dispatch(EVENTS.PENDING_CHANGES_UPDATE, { changes: null })
      },
      undoItem: (index) => {
        const current = stateRef.current.pendingChanges
        dispatch(EVENTS.PENDING_CHANGES_UPDATE, {
          changes: removePendingChangeAtIndex(current, index),
        })
      },
      scrollToItem: (id) => {
        const row = findTestCaseRow(id)
        row?.scrollIntoView({ behavior: "smooth", block: "center" })
        requestCoviHighlight(id)
      },
    })
    registrationRef.current = registration
    return unregister
  }, [dispatch])

  const snapshot = useMemo(() => {
    const diffs = getPendingDiffSummaries(pendingChanges, testCases) ?? {}
    const items = pendingChanges
      ? pendingChanges.data.map((entry, index) => {
          const id: string =
            pendingChanges.type === "DELETE"
              ? (entry as string)
              : (entry as { id: string }).id
          const pendingTestCase =
            pendingChanges.type === "ADD"
              ? (entry as EditableTestCase)
              : testCases.find((candidate) => candidate.id === id)
          return {
            id,
            label: testCaseLabel(pendingTestCase, index),
            added: diffs[id]?.added ?? 0,
            removed: diffs[id]?.removed ?? 0,
          }
        })
      : []
    return {
      active: true,
      testSetId,
      testCaseIds: testCases.map((testCase) => testCase.id),
      pendingType: pendingChanges?.type ?? null,
      items,
    }
  }, [pendingChanges, testCases, testSetId])

  useEffect(() => {
    if (registrationRef.current) {
      publishCoviTestSetEditorSnapshot(registrationRef.current, snapshot)
    }
  }, [snapshot])

  return null
}
